/**
 * Faction Assignment Utilities
 * 
 * Region-based faction assignment system with cellular automata clustering
 * Creates contiguous faction territories with natural wilderness borders
 */

import { AxialCoord, FactionId, TerrainHex, TerrainType } from '../types/terrain';
import { hexNeighbors, hexKey, isInBounds } from './hexMath';

/**
 * Assign faction to hex based on regional location
 * Each faction controls ALL terrain types within their region
 * 
 * OVERLOAD 1: With explicit map dimensions (recommended for new code)
 * @param hex - Axial coordinates of the hex
 * @param mapWidth - Width of the map in hexes
 * @param mapHeight - Height of the map in hexes
 * @param regionId - Optional region ID (1-6), if not provided will be calculated
 * @returns FactionId or null for wilderness
 */
export function assignFactionToHex(
  hex: AxialCoord, 
  mapWidth: number,
  mapHeight: number,
  regionId?: number
): FactionId;

/**
 * OVERLOAD 2: Legacy signature for backward compatibility
 * Assumes 200x200 map dimensions
 * @param hex - Axial coordinates of the hex
 * @param regionId - Optional region ID (1-6)
 * @returns FactionId or null for wilderness
 */
export function assignFactionToHex(
  hex: AxialCoord,
  regionId?: number
): FactionId;

// Implementation
export function assignFactionToHex(
  hex: AxialCoord, 
  mapWidthOrRegionId?: number,
  mapHeight?: number,
  regionId?: number
): FactionId {
  // Determine which overload was called
  let actualMapWidth: number;
  let actualMapHeight: number;
  let actualRegionId: number | undefined;
  
  if (mapHeight !== undefined) {
    // Overload 1: (hex, mapWidth, mapHeight, regionId?)
    actualMapWidth = mapWidthOrRegionId!;
    actualMapHeight = mapHeight;
    actualRegionId = regionId;
  } else {
    // Overload 2: (hex, regionId?) - legacy
    actualMapWidth = 200;
    actualMapHeight = 200;
    actualRegionId = mapWidthOrRegionId;
  }
  
  // Assign based on region
  const region = actualRegionId || determineRegionFromCoords(hex, actualMapWidth, actualMapHeight);
  
  // Assign faction based purely on region (no random scatter)
  // Wilderness will be added through clustering algorithm
  switch(region) {
    case 1: // Northern Highlands
      return 'highland-clans';
    case 2: // Central Valley
      return 'stoneheart-clans';
    case 3: // Central Uplands Spine
      return 'hearth-folk';
    case 4: // Western Massif
      return 'sea-kings';
    case 5: // South-Western Peninsula
      return 'wildwood-folk';
    case 6: // South-Eastern Plains
      return 'riverbend-kingdoms';
    default:
      return null; // wilderness
  }
}

/**
 * Determine topographical region from hex coordinates
 * Divides the map into 6 regions in a 3x2 grid
 * 
 * Map layout (scales to any size):
 * [1: Northern Highlands] [2: Central Valley]
 * [3: Central Uplands]    [4: Western Massif]
 * [5: SW Peninsula]       [6: SE Plains]
 * 
 * @param hex - Axial coordinates of the hex
 * @param mapWidth - Width of the map in hexes (required)
 * @param mapHeight - Height of the map in hexes (required)
 */
export function determineRegionFromCoords(hex: AxialCoord, mapWidth: number, mapHeight: number): number {
  // Normalize coordinates to 0-1 range
  const normalizedQ = hex.q / mapWidth;
  const normalizedR = hex.r / mapHeight;
  
  // Divide into 3 rows (north, central, south)
  let row: number;
  if (normalizedR < 0.33) {
    row = 0; // North
  } else if (normalizedR < 0.67) {
    row = 1; // Central
  } else {
    row = 2; // South
  }
  
  // Divide into 2 columns (west, east)
  const col = normalizedQ < 0.5 ? 0 : 1;
  
  // Map to region IDs
  // Row 0 (North): [1, 2]
  // Row 1 (Central): [3, 4]
  // Row 2 (South): [5, 6]
  const regionMap = [
    [1, 2],  // North: Highland Clans, Stoneheart
    [3, 4],  // Central: Hearth-Folk, Sea-Kings
    [5, 6],  // South: Wildwood Folk, Riverbend
  ];
  
  return regionMap[row][col];
}

/**
 * Apply cellular automata to create contiguous faction territories
 * with natural wilderness zones along borders and in remote areas
 * 
 * @param hexes - Map of all terrain hexes
 * @param width - Map width in hexes
 * @param height - Map height in hexes
 * @param wildernessPercentage - Target percentage of land to be wilderness (default 25%)
 * @param iterations - Number of cellular automata smoothing passes (default 3)
 */
export function applyFactionClustering(
  hexes: Map<string, TerrainHex>,
  width: number,
  height: number,
  wildernessPercentage: number = 0.25,
  iterations: number = 3
): void {
  // Step 1: Assign all land hexes to their regional faction (100% coverage)
  console.log('  Step 1: Assigning regional factions...');
  for (const hex of hexes.values()) {
    if (hex.terrain === TerrainType.Ocean) continue;
    hex.faction = assignFactionToHex({ q: hex.q, r: hex.r }, width, height);
  }
  
  // Step 2: Create wilderness seeds at region borders and remote areas
  console.log('  Step 2: Creating wilderness border zones...');
  const landHexes = Array.from(hexes.values()).filter(h => h.terrain !== TerrainType.Ocean);
  
  // Identify border hexes (where neighbors have different factions)
  for (const hex of landHexes) {
    const neighbors = hexNeighbors({ q: hex.q, r: hex.r })
      .filter(n => isInBounds(n, width, height))
      .map(n => hexes.get(hexKey(n)))
      .filter((h): h is TerrainHex => h !== undefined && h.terrain !== TerrainType.Ocean);
    
    const differentFactions = neighbors.filter(n => n.faction !== hex.faction).length;
    
    // Higher chance of wilderness at borders
    if (differentFactions >= 2) {
      const borderChance = 0.4 + (differentFactions * 0.1); // 40-60% based on neighbors
      if (Math.random() < borderChance) {
        hex.faction = null; // Convert to wilderness
      }
    }
    
    // Some random interior wilderness (mountains, tundra more likely)
    if (hex.terrain === TerrainType.Mountain && Math.random() < 0.3) {
      hex.faction = null;
    }
    if (hex.terrain === TerrainType.Tundra && Math.random() < 0.25) {
      hex.faction = null;
    }
  }
  
  // Step 3: Apply cellular automata to smooth wilderness boundaries
  console.log('  Step 3: Smoothing faction boundaries with cellular automata...');
  for (let iter = 0; iter < iterations; iter++) {
    const updates = new Map<string, FactionId>();
    
    for (const hex of landHexes) {
      if (hex.terrain === TerrainType.Ocean) continue;
      
      const neighbors = hexNeighbors({ q: hex.q, r: hex.r })
        .filter(n => isInBounds(n, width, height))
        .map(n => hexes.get(hexKey(n)))
        .filter((h): h is TerrainHex => h !== undefined && h.terrain !== TerrainType.Ocean);
      
      if (neighbors.length === 0) continue;
      
      // Count faction types in neighbors
      const factionCounts = new Map<FactionId, number>();
      for (const neighbor of neighbors) {
        const faction = neighbor.faction || null;
        const count = factionCounts.get(faction) || 0;
        factionCounts.set(faction, count + 1);
      }
      
      // Find majority faction
      let majorityFaction: FactionId = hex.faction || null;
      let maxCount = 0;
      for (const [faction, count] of factionCounts) {
        if (count > maxCount) {
          maxCount = count;
          majorityFaction = faction;
        }
      }
      
      // If majority differs significantly, adopt it (creates contiguous regions)
      if (maxCount >= 4 && majorityFaction !== hex.faction) {
        updates.set(hexKey({ q: hex.q, r: hex.r }), majorityFaction);
      }
    }
    
    // Apply updates
    for (const [key, faction] of updates) {
      const hex = hexes.get(key);
      if (hex) hex.faction = faction;
    }
    
    console.log(`    Iteration ${iter + 1}: ${updates.size} hexes updated`);
  }
  
  // Count final distribution
  const wildernessCount = landHexes.filter(h => h.faction === null).length;
  const wildernessPercent = (wildernessCount / landHexes.length * 100).toFixed(1);
  console.log(`  Final wilderness: ${wildernessCount} hexes (${wildernessPercent}%)`);
}

/**
 * Get faction display name
 */
export function getFactionName(faction: FactionId): string {
  switch (faction) {
    case 'highland-clans':
      return 'Highland Clans';
    case 'stoneheart-clans':
      return 'Stoneheart Clans';
    case 'sea-kings':
      return 'Sea-Kings';
    case 'hearth-folk':
      return 'Hearth-Folk';
    case 'wildwood-folk':
      return 'Wildwood Folk';
    case 'riverbend-kingdoms':
      return 'Riverbend Kingdoms';
    case null:
      return 'Wilderness';
    default:
      return 'Unknown';
  }
}

